import { collectionSchema, type Collection } from "@shared/schemas/common";
import {
  compareResponseSchema,
  saveModelResponseSchema,
  scenarioDetailSchema,
  scenarioDocumentSchema,
  scenarioRowSchema,
  scenarioVersionDetailSchema,
  scenarioVersionRowSchema,
  versionConflictDetailsSchema,
  type CompareResponse,
  type CreateScenarioRequest,
  type CreateShareRequest,
  type PatchScenarioRequest,
  type SaveModelRequest,
  type SaveModelResponse,
  type ScenarioDetail,
  type ScenarioDocumentDto,
  type ScenarioRow,
  type ScenarioVersionDetail,
  type ScenarioVersionRow,
  type VisibilityName,
} from "@shared/schemas/scenario";
import {
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  noContentSchema,
  queryString,
} from "@client/lib/api";

/** The scenario half of docs/06-api.md. */

const scenarioCollectionSchema = collectionSchema(scenarioRowSchema);

export interface ScenarioListFilters {
  mine?: boolean;
  visibility?: VisibilityName;
  q?: string;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
}

export const listScenarios = (
  productId: string,
  filters: ScenarioListFilters = {},
): Promise<Collection<ScenarioRow>> =>
  apiGet(
    `/products/${encodeURIComponent(productId)}/scenarios${queryString({
      mine: filters.mine ? "true" : undefined,
      visibility: filters.visibility,
      q: filters.q,
      includeArchived: filters.includeArchived ? "true" : undefined,
      limit: filters.limit,
      offset: filters.offset,
    })}`,
    scenarioCollectionSchema,
  );

export const createScenario = (
  productId: string,
  input: CreateScenarioRequest,
): Promise<ScenarioDetail> =>
  apiPost(`/products/${encodeURIComponent(productId)}/scenarios`, scenarioDetailSchema, input);

export const getScenario = (id: string): Promise<ScenarioDetail> =>
  apiGet(`/scenarios/${encodeURIComponent(id)}`, scenarioDetailSchema);

export const patchScenario = (id: string, input: PatchScenarioRequest): Promise<ScenarioDetail> =>
  apiPatch(`/scenarios/${encodeURIComponent(id)}`, scenarioDetailSchema, input);

/**
 * The whole-model save. The `version` in the body is the one the editor loaded;
 * a `409 VERSION_CONFLICT` means someone else saved first, and carries their
 * scenario so the conflict dialog can show a real diff — see `conflictFrom`.
 */
export const saveScenarioModel = (
  id: string,
  input: SaveModelRequest,
): Promise<SaveModelResponse> =>
  apiPut(`/scenarios/${encodeURIComponent(id)}/model`, saveModelResponseSchema, input);

/**
 * The server's copy out of a 409, or `null` for any other failure.
 *
 * Parsed rather than cast: a conflict body that does not hold a whole scenario
 * would otherwise reach the dialog as `undefined.model` three components later,
 * and the honest answer there is to show the plain error instead.
 */
export function conflictFrom(
  error: unknown,
): { currentVersion: number; current: ScenarioDetail } | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const parsed = versionConflictDetailsSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

export const duplicateScenario = (id: string, name?: string): Promise<ScenarioDetail> =>
  apiPost(`/scenarios/${encodeURIComponent(id)}/duplicate`, scenarioDetailSchema, { name });

export const archiveScenario = (id: string, restore = false): Promise<ScenarioDetail> =>
  apiPost(
    `/scenarios/${encodeURIComponent(id)}/archive${queryString({ restore: restore ? "true" : undefined })}`,
    scenarioDetailSchema,
  );

export const shareScenario = (id: string, input: CreateShareRequest): Promise<ScenarioDetail> =>
  apiPost(`/scenarios/${encodeURIComponent(id)}/shares`, scenarioDetailSchema, input);

/** 204; the dialog refetches the scenario to redraw its share list. */
export const unshareScenario = (id: string, userId: string): Promise<undefined> =>
  apiDelete(
    `/scenarios/${encodeURIComponent(id)}/shares/${encodeURIComponent(userId)}`,
    noContentSchema,
  );

/* ---------------------------------------------------------------- versions */

const versionCollectionSchema = collectionSchema(scenarioVersionRowSchema);

/** Metadata only — the snapshots are far too big to list. */
export const listScenarioVersions = (
  id: string,
  limit = 50,
): Promise<Collection<ScenarioVersionRow>> =>
  apiGet(
    `/scenarios/${encodeURIComponent(id)}/versions${queryString({ limit })}`,
    versionCollectionSchema,
  );

export const getScenarioVersion = (id: string, version: number): Promise<ScenarioVersionDetail> =>
  apiGet(`/scenarios/${encodeURIComponent(id)}/versions/${version}`, scenarioVersionDetailSchema);

/**
 * Restore saves the old model *forward* as a new version. The response is the
 * same shape a model save returns, so the caller writes it into the detail cache
 * exactly as it would after any other save.
 */
export const restoreScenarioVersion = (id: string, version: number): Promise<SaveModelResponse> =>
  apiPost(
    `/scenarios/${encodeURIComponent(id)}/versions/${version}/restore`,
    saveModelResponseSchema,
  );

/* ------------------------------------------------------- export and import */

export const exportScenario = (id: string): Promise<ScenarioDocumentDto> =>
  apiGet(`/scenarios/${encodeURIComponent(id)}/export`, scenarioDocumentSchema);

/**
 * The body is the document itself — a canonical export, or anything else
 * `parseScenarioDocument` recognises. The dialog parses the file in the browser
 * first, so by the time this runs the user has already seen what they are about
 * to create.
 */
export const importScenario = (productId: string, document: unknown): Promise<ScenarioDetail> =>
  apiPost(
    `/products/${encodeURIComponent(productId)}/scenarios/import`,
    scenarioDetailSchema,
    document,
  );

/* ----------------------------------------------------------------- compare */

export const compareScenarios = (ids: readonly string[]): Promise<CompareResponse> =>
  apiPost("/scenarios/compare", compareResponseSchema, { ids: [...ids] });
